"use client";
import React, { useState, useEffect, Fragment } from "react";
import { PrismicNextImage } from "@prismicio/next";
import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";

const MasonryGallery = ({ images }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isModalOpen) {
        if (event.key === "ArrowRight") {
          nextImage();
        } else if (event.key === "ArrowLeft") {
          prevImage();
        } else if (event.key === "Escape") {
          closeModal();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const columns = 4; // Number of columns in the masonry layout
  const columnWrapper = {};
  const result = [];

  // Create column arrays
  for (let i = 0; i < columns; i++) {
    columnWrapper[`column${i}`] = [];
  }

  // Distribute images into columns
  for (let i = 0; i < images.length; i++) {
    const columnIndex = i % columns;
    columnWrapper[`column${columnIndex}`].push({ ...images[i], index: i });
  }

  // Wrap column content in divs
  for (let i = 0; i < columns; i++) {
    result.push(
      <div key={i} className="grid gap-4">
        {columnWrapper[`column${i}`].map((imageObject) => (
          <div
            key={imageObject.index}
            className="cursor-pointer flex transform transition-transform duration-300 hover:scale-105"
            onClick={() => openModal(imageObject.index)}
          >
            <PrismicNextImage
              field={imageObject.image}
              alt="Image Gallery"
              className="h-auto max-w-full rounded-lg"
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Masonry Gallery */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{result}</div>

      {/* Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          className="fixed inset-0 z-50 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen">
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 bg-black bg-opacity-80"
                onClick={closeModal}
              />
            </Transition.Child>

            <button
              onClick={closeModal}
              className="absolute top-1 right-1 p-2 bg-transparent"
            >
              <Icon icon="mingcute:close-fill" className="w-6 h-6 text-white" />
            </button>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <div className="relative mx-auto w-full h-full md:max-w-3xl p-10">
                <PrismicNextImage
                  field={images[currentImageIndex].image}
                  alt="Image Gallery"
                  sizes="(min-width: 1024px) 800px, 90vw"
                  className="w-full h-full"
                />
                <button
                  className="absolute top-1/2 left-20 text-2xl text-black bg-white rounded-full px-4 py-2 drop-shadow-md"
                  onClick={prevImage}
                >
                  &#8249;
                </button>
                <button
                  className="absolute top-1/2 right-20 text-2xl text-black bg-white rounded-full px-4 py-2 drop-shadow-md"
                  onClick={nextImage}
                >
                  &#8250;
                </button>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MasonryGallery;
